/**
 * Readings Cache Service
 * Provides high-level API for managing encrypted sensor readings cache
 * Handles smart merging of cached and fresh data
 */

import {
  encryptedReadingsCache,
  TimeRange,
  ReadingsCacheStats
} from '../../utils/encryptedReadingsCache'
import { Reading } from '../../hooks/useDeviceReadings'
import { cacheSettingsService } from './cacheSettingsService'

/**
 * Result of a cache-aware fetch operation
 */
export interface CacheFetchResult {
  readings: Reading[]
  source: 'cache' | 'api' | 'mixed'
  cacheHitRatio: number  // 0.0 to 1.0
  missingRanges: TimeRange[]
  metadata?: any  // Sensor metadata (productName, sensorName, units)
}

/**
 * Readings Cache Service
 * Smart caching layer for sensor readings with encryption
 */
class ReadingsCacheService {
  /**
   * Get readings with cache-first strategy
   *
   * Returns cached readings immediately if available,
   * along with information about what ranges need to be fetched from API
   *
   * @param sigfoxId - Device ID
   * @param email - User email for encryption
   * @param from - Start timestamp (Unix seconds)
   * @param to - End timestamp (Unix seconds)
   * @returns Cached readings and missing ranges to fetch
   */
  async getCachedReadings(
    sigfoxId: string,
    email: string,
    from: number,
    to: number
  ): Promise<CacheFetchResult> {
    // Check if cache is enabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return {
        readings: [],
        source: 'api',
        cacheHitRatio: 0,
        missingRanges: [{ from, to }]
      }
    }

    try {
      const result = await encryptedReadingsCache.getCachedReadings(
        sigfoxId,
        email,
        from,
        to
      )

      // Calculate cache hit ratio
      const requestedRange = to - from
      const missingRange = result.missingRanges.reduce(
        (sum, range) => sum + (range.to - range.from),
        0
      )
      const cacheHitRatio =
        requestedRange > 0
          ? Math.max(0, Math.min(1, 1 - missingRange / requestedRange))
          : 0

      const source = result.cacheHit
        ? 'cache'
        : result.readings.length > 0
        ? 'mixed'
        : 'api'

      return {
        readings: result.readings,
        source,
        cacheHitRatio,
        missingRanges: result.missingRanges,
        metadata: result.metadata
      }
    } catch (error) {
      console.error('Error getting cached readings:', error)
      return {
        readings: [],
        source: 'api',
        cacheHitRatio: 0,
        missingRanges: [{ from, to }]
      }
    }
  }

  /**
   * Cache new readings from API
   *
   * @param sigfoxId - Device ID
   * @param email - User email for encryption
   * @param readings - Readings to cache
   * @param timeRange - Time range these readings cover
   * @param metadata - Optional sensor metadata (productName, sensorName, units)
   */
  async cacheReadings(
    sigfoxId: string,
    email: string,
    readings: Reading[],
    timeRange: TimeRange,
    metadata?: any
  ): Promise<void> {
    // Skip caching if disabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return
    }

    try {
      await encryptedReadingsCache.cacheReadings(
        sigfoxId,
        email,
        readings,
        timeRange,
        metadata
      )
    } catch (error) {
      console.error('Error caching readings:', error)
      // Don't throw - caching is non-critical
    }
  }

  /**
   * Merge cached and fresh readings
   *
   * Combines readings from cache and API, removing duplicates
   * and sorting by timestamp
   *
   * @param cachedReadings - Readings from cache
   * @param freshReadings - Readings from API
   * @returns Merged and sorted readings
   */
  mergeReadings(
    cachedReadings: Reading[],
    freshReadings: Reading[]
  ): Reading[] {
    // Use Map to deduplicate by timestamp
    const readingMap = new Map<number, Reading>()

    // Add cached readings first
    cachedReadings.forEach(reading => {
      readingMap.set(reading.timestamp, reading)
    })

    // Add fresh readings (overwrites any duplicates with fresh data)
    freshReadings.forEach(reading => {
      readingMap.set(reading.timestamp, reading)
    })

    // Convert to array and sort by timestamp
    return Array.from(readingMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp
    )
  }

  /**
   * Get cached time ranges for a device
   *
   * @param sigfoxId - Device ID
   * @param email - User email for decryption
   * @returns Array of cached time ranges
   */
  async getCachedTimeRanges(
    sigfoxId: string,
    email: string
  ): Promise<TimeRange[]> {
    try {
      return await encryptedReadingsCache.getCachedTimeRanges(sigfoxId, email)
    } catch (error) {
      console.error('Error getting cached time ranges:', error)
      return []
    }
  }

  /**
   * Get all sensor keys for a device from cache
   * Useful for discovering what sensors exist without an API call
   *
   * @param sigfoxId - Device ID
   * @param email - User email
   * @returns Array of sensor keys (e.g., ["A1-13", "B2-12"])
   */
  async getDeviceSensors(
    sigfoxId: string,
    email: string
  ): Promise<string[]> {
    // Return empty array if cache disabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return []
    }

    try {
      return await encryptedReadingsCache.getDeviceSensors(sigfoxId, email)
    } catch (error) {
      console.error('Error getting device sensors:', error)
      return []
    }
  }

  /**
   * Get cached readings for multiple sensors in batch
   * More efficient than calling getCachedReadings for each sensor individually
   *
   * @param sigfoxId - Device ID
   * @param sensorKeys - Array of sensor keys to fetch
   * @param email - User email for encryption
   * @param from - Start timestamp (Unix seconds)
   * @param to - End timestamp (Unix seconds)
   * @returns Map of sensor key to cache fetch result
   */
  async getBatchCachedReadings(
    sigfoxId: string,
    sensorKeys: string[],
    email: string,
    from: number,
    to: number
  ): Promise<Map<string, CacheFetchResult>> {
    // Return empty Map if cache disabled
    if (!cacheSettingsService.isCacheEnabled()) {
      return new Map()
    }

    try {
      // Execute all cache lookups in parallel
      const cachePromises = sensorKeys.map(async (sensorKey) => {
        const cacheKey = `${sigfoxId}-${sensorKey}`
        const result = await this.getCachedReadings(cacheKey, email, from, to)
        return { sensorKey, result }
      })

      const results = await Promise.all(cachePromises)

      // Convert to Map
      const resultsMap = new Map<string, CacheFetchResult>()
      for (const { sensorKey, result } of results) {
        resultsMap.set(sensorKey, result)
      }

      return resultsMap
    } catch (error) {
      console.error('Error getting batch cached readings:', error)
      return new Map()
    }
  }

  /**
   * Invalidate cache for a specific device
   * Useful when device configuration changes or data needs refresh
   *
   * @param sigfoxId - Device ID
   * @param email - User email
   */
  async invalidateDevice(sigfoxId: string, email: string): Promise<void> {
    try {
      console.log(`🗑️ Invalidating readings cache for device ${sigfoxId}`)
      await encryptedReadingsCache.clearDevice(sigfoxId, email)
    } catch (error) {
      console.error('Error invalidating device cache:', error)
    }
  }

  /**
   * Invalidate all cache for a user
   * Useful on logout or when user data changes
   *
   * @param email - User email
   */
  async invalidateUser(email: string): Promise<void> {
    try {
      console.log(`🗑️ Invalidating all readings cache for user`)
      await encryptedReadingsCache.clearUserCache(email)
    } catch (error) {
      console.error('Error invalidating user cache:', error)
    }
  }

  /**
   * Clear all cached readings
   * Useful for debugging or cleanup
   */
  async clearAllCache(): Promise<void> {
    try {
      console.log('🗑️ Clearing all readings cache')
      await encryptedReadingsCache.clearAll()
    } catch (error) {
      console.error('Error clearing all cache:', error)
    }
  }

  /**
   * Get cache statistics
   *
   * @param email - Optional user email for user-specific stats
   * @returns Cache statistics
   */
  async getCacheStats(email?: string): Promise<ReadingsCacheStats> {
    try {
      return await encryptedReadingsCache.getCacheStats(email)
    } catch (error) {
      console.error('Error getting cache stats:', error)
      return {
        totalDevices: 0,
        totalSensors: 0,
        totalRanges: 0,
        totalReadings: 0,
        oldestTimestamp: null,
        newestTimestamp: null,
        cacheSize: 0
      }
    }
  }

  /**
   * Optimize time ranges by filling small gaps
   *
   * If two cached ranges are very close (e.g., 1 hour apart),
   * it might be more efficient to fetch the whole range in one API call
   * rather than multiple small requests
   *
   * @param ranges - Missing time ranges
   * @param maxGapSeconds - Maximum gap to fill (default: 1 hour)
   * @returns Optimized ranges
   */
  optimizeFetchRanges(
    ranges: TimeRange[],
    maxGapSeconds: number = 3600
  ): TimeRange[] {
    if (ranges.length <= 1) return ranges

    const optimized: TimeRange[] = []
    let current = { ...ranges[0] }

    for (let i = 1; i < ranges.length; i++) {
      const next = ranges[i]
      const gap = next.from - current.to

      // If gap is small, merge the ranges
      if (gap <= maxGapSeconds) {
        current.to = next.to
      } else {
        optimized.push(current)
        current = { ...next }
      }
    }

    optimized.push(current)
    return optimized
  }

  /**
   * Estimate cache efficiency for a device
   *
   * @param sigfoxId - Device ID
   * @param email - User email
   * @param from - Start of analysis period
   * @param to - End of analysis period
   * @returns Efficiency metrics
   */
  async estimateCacheEfficiency(
    sigfoxId: string,
    email: string,
    from: number,
    to: number
  ): Promise<{
    coverage: number  // 0.0 to 1.0
    ranges: number
    gaps: number
  }> {
    try {
      const ranges = await this.getCachedTimeRanges(sigfoxId, email)

      if (ranges.length === 0) {
        return { coverage: 0, ranges: 0, gaps: 0 }
      }

      const requestedDuration = to - from
      let cachedDuration = 0

      // Calculate how much of the requested range is cached
      for (const range of ranges) {
        const overlapStart = Math.max(range.from, from)
        const overlapEnd = Math.min(range.to, to)

        if (overlapStart < overlapEnd) {
          cachedDuration += overlapEnd - overlapStart
        }
      }

      const coverage = requestedDuration > 0 ? cachedDuration / requestedDuration : 0
      const gaps = ranges.length > 1 ? ranges.length - 1 : 0

      return {
        coverage: Math.max(0, Math.min(1, coverage)),
        ranges: ranges.length,
        gaps
      }
    } catch (error) {
      console.error('Error estimating cache efficiency:', error)
      return { coverage: 0, ranges: 0, gaps: 0 }
    }
  }

  /**
   * Prefetch readings for a device
   * Useful for warming up cache before user needs the data
   *
   * @param sigfoxId - Device ID
   * @param email - User email
   * @param from - Start timestamp
   * @param to - End timestamp
   * @param fetchFn - Function to fetch readings from API
   */
  async prefetchReadings(
    sigfoxId: string,
    email: string,
    from: number,
    to: number,
    fetchFn: (from: number, to: number) => Promise<Reading[]>
  ): Promise<void> {
    try {
      const result = await this.getCachedReadings(sigfoxId, email, from, to)

      if (result.missingRanges.length === 0) {
        console.log(`✅ Prefetch: Already cached for ${sigfoxId}`)
        return
      }

      // Fetch missing ranges
      for (const range of result.missingRanges) {
        const readings = await fetchFn(range.from, range.to)
        await this.cacheReadings(sigfoxId, email, readings, range)
      }

      console.log(`✅ Prefetch complete for ${sigfoxId}`)
    } catch (error) {
      console.error('Error prefetching readings:', error)
    }
  }
}

// Export singleton instance
export const readingsCacheService = new ReadingsCacheService()
export type { TimeRange }
